// src/pages/PayoutsPage.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  addDoc,
} from "firebase/firestore";
import { firestore } from "../firebase";

// Helper function to convert Firestore value to Date.
const getDateFromValue = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    return value.toDate();
  }
  return new Date(value);
};

// Dynamic cutoff for payouts (placeholder)
// For demonstration, we use a fixed cutoff date; replace with your dynamic logic.
const getCutoffDate = () => {
  return new Date("2025-03-12");
};

// -------------------
// Fetching Shop Payouts
// -------------------
const fetchShopPayouts = async () => {
  const shopsSnapshot = await getDocs(collection(firestore, "rentalShops"));
  const shopsDue = [];
  const cutoffDate = getCutoffDate();

  for (const shopDoc of shopsSnapshot.docs) {
    const shop = { id: shopDoc.id, ...shopDoc.data() };
    let lastPayoutDate = null;
    if (shop.payouts && shop.payouts.length > 0) {
      const sortedPayouts = shop.payouts.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      lastPayoutDate = new Date(sortedPayouts[0].date);
    } else {
      lastPayoutDate = new Date("2025-01-01");
    }

    const alreadyPaid =
      shop.payouts && shop.payouts.some((p) => new Date(p.date) >= cutoffDate);
    if (alreadyPaid) continue;

    const shopBookingsSnapshot = await getDocs(
      collection(shopDoc.ref, "bookings")
    );
    let dueAmount = 0;
    let bookingIds = [];

    shopBookingsSnapshot.forEach((bookingDoc) => {
      const booking = bookingDoc.data();
      if (!booking.startDate || !booking.endDate || !booking.dailyPrice) return;
      const startDate = getDateFromValue(booking.startDate);
      const endDate = getDateFromValue(booking.endDate);
      if (!startDate || !endDate) return;
      const method = booking.bookingMethod
        ? booking.bookingMethod.toLowerCase()
        : "";
      if (method !== "online") return;
      if (startDate > lastPayoutDate && startDate <= cutoffDate) {
        const lengthInDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
        if (lengthInDays > 0) {
          // For shops, payout is 85% of booking income.
          dueAmount += lengthInDays * booking.dailyPrice * 0.85;
          bookingIds.push(bookingDoc.id);
        }
      }
    });

    if ((!shop.payouts || shop.payouts.length === 0) && dueAmount === 0) {
      const allBookingsSnapshot = await getDocs(
        collection(shopDoc.ref, "bookings")
      );
      allBookingsSnapshot.forEach((bookingDoc) => {
        const booking = bookingDoc.data();
        if (!booking.startDate || !booking.endDate || !booking.dailyPrice)
          return;
        const startDate = getDateFromValue(booking.startDate);
        const endDate = getDateFromValue(booking.endDate);
        if (!startDate || !endDate) return;
        const method = booking.bookingMethod
          ? booking.bookingMethod.toLowerCase()
          : "";
        if (method !== "online") return;
        if (startDate <= cutoffDate) {
          const lengthInDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
          if (lengthInDays > 0) {
            dueAmount += lengthInDays * booking.dailyPrice * 0.85;
            bookingIds.push(bookingDoc.id);
          }
        }
      });
    }

    if (dueAmount > 0) {
      shopsDue.push({
        id: shop.id,
        name: shop.name || "Unnamed Shop",
        amountDue: dueAmount,
        lastPayoutDate,
        cutoffDate,
        bookingIds,
      });
    }
  }
  return shopsDue;
};

// -------------------
// Fetching Affiliate Payouts
// -------------------
const fetchAffiliatePayouts = async () => {
  const affiliatesSnapshot = await getDocs(collection(firestore, "affiliates"));
  const affiliatesDue = [];
  const cutoffDate = getCutoffDate();

  for (const affDoc of affiliatesSnapshot.docs) {
    const affiliate = { id: affDoc.id, ...affDoc.data() };
    let lastPayoutDate = null;
    if (affiliate.payouts && affiliate.payouts.length > 0) {
      const sortedPayouts = affiliate.payouts.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      lastPayoutDate = new Date(sortedPayouts[0].date);
    } else {
      lastPayoutDate = new Date("2025-01-01");
    }
    const alreadyPaid =
      affiliate.payouts &&
      affiliate.payouts.some((p) => new Date(p.date) >= cutoffDate);
    if (alreadyPaid) continue;

    let dueAmount = 0;
    let bookingIds = [];

    if (
      affiliate.shops &&
      Array.isArray(affiliate.shops) &&
      affiliate.shops.length > 0
    ) {
      for (const shopId of affiliate.shops) {
        const shopRef = doc(firestore, "rentalShops", shopId);
        const shopBookingsSnapshot = await getDocs(
          collection(shopRef, "bookings")
        );
        shopBookingsSnapshot.forEach((bookingDoc) => {
          const booking = bookingDoc.data();
          if (!booking.startDate || !booking.endDate || !booking.dailyPrice)
            return;
          const startDate = getDateFromValue(booking.startDate);
          const endDate = getDateFromValue(booking.endDate);
          if (!startDate || !endDate) return;
          const method = booking.bookingMethod
            ? booking.bookingMethod.toLowerCase()
            : "";
          if (method !== "online") return;
          if (startDate > lastPayoutDate && startDate <= cutoffDate) {
            const lengthInDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
            if (lengthInDays > 0) {
              // Affiliate commission is 2% of booking income.
              dueAmount += lengthInDays * booking.dailyPrice * 0.02;
              bookingIds.push(bookingDoc.id);
            }
          }
        });
      }
      if (
        (!affiliate.payouts || affiliate.payouts.length === 0) &&
        dueAmount === 0
      ) {
        for (const shopId of affiliate.shops) {
          const shopRef = doc(firestore, "rentalShops", shopId);
          const shopBookingsSnapshot = await getDocs(
            collection(shopRef, "bookings")
          );
          shopBookingsSnapshot.forEach((bookingDoc) => {
            const booking = bookingDoc.data();
            if (!booking.startDate || !booking.endDate || !booking.dailyPrice)
              return;
            const startDate = getDateFromValue(booking.startDate);
            const endDate = getDateFromValue(booking.endDate);
            if (!startDate || !endDate) return;
            const method = booking.bookingMethod
              ? booking.bookingMethod.toLowerCase()
              : "";
            if (method !== "online") return;
            if (startDate <= cutoffDate) {
              const lengthInDays =
                (endDate - startDate) / (1000 * 60 * 60 * 24);
              if (lengthInDays > 0) {
                dueAmount += lengthInDays * booking.dailyPrice * 0.02;
                bookingIds.push(bookingDoc.id);
              }
            }
          });
        }
      }
    }
    if (dueAmount > 0) {
      affiliatesDue.push({
        id: affiliate.id,
        name: affiliate.name || "Unnamed Affiliate",
        amountDue: dueAmount,
        lastPayoutDate,
        cutoffDate,
        bookingIds,
      });
    }
  }
  return affiliatesDue;
};

const PayoutsPage = () => {
  const [shopPayouts, setShopPayouts] = useState([]);
  const [affiliatePayouts, setAffiliatePayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toPhilData, setToPhilData] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const shopsDue = await fetchShopPayouts();
      const affiliatesDue = await fetchAffiliatePayouts();
      setShopPayouts(shopsDue);
      setAffiliatePayouts(affiliatesDue);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Define markShopPayoutDone so it is available in JSX.
  const markShopPayoutDone = async (shop) => {
    try {
      const shopRef = doc(firestore, "rentalShops", shop.id);
      const newPayout = {
        date: new Date().toISOString(),
        amount: shop.amountDue,
      };
      await updateDoc(shopRef, { payouts: arrayUnion(newPayout) });
      await addDoc(collection(firestore, "transactions"), {
        type: "shopPayout",
        shopId: shop.id,
        amount: shop.amountDue,
        bookingIds: shop.bookingIds,
        date: new Date().toISOString(),
      });
      const shopsDue = await fetchShopPayouts();
      setShopPayouts(shopsDue);
    } catch (error) {
      console.error("Error marking shop payout as done:", error);
    }
  };

  // Define markAffiliatePayoutDone.
  const markAffiliatePayoutDone = async (affiliate) => {
    try {
      const affRef = doc(firestore, "affiliates", affiliate.id);
      const newPayout = {
        date: new Date().toISOString(),
        amount: affiliate.amountDue,
      };
      await updateDoc(affRef, { payouts: arrayUnion(newPayout) });
      await addDoc(collection(firestore, "transactions"), {
        type: "affiliatePayout",
        affiliateId: affiliate.id,
        amount: affiliate.amountDue,
        bookingIds: affiliate.bookingIds,
        date: new Date().toISOString(),
      });
      const affiliatesDue = await fetchAffiliatePayouts();
      setAffiliatePayouts(affiliatesDue);
    } catch (error) {
      console.error("Error marking affiliate payout as done:", error);
    }
  };

  // Calculate totals for payments to Philippines.
  const totalShopDue = shopPayouts.reduce(
    (sum, shop) => sum + shop.amountDue,
    0
  );
  const totalAffiliateDue = affiliatePayouts.reduce(
    (sum, aff) => sum + aff.amountDue,
    0
  );
  const totalEly = (totalShopDue / 85) * 100 * 0.03; // 3% of shop online revenue.
  const allBookingIds = [
    ...new Set([
      ...shopPayouts.flatMap((shop) => shop.bookingIds || []),
      ...affiliatePayouts.flatMap((aff) => aff.bookingIds || []),
    ]),
  ];

  // Confirm payments to Philippines.
  const confirmToPhilippinesPayments = async () => {
    try {
      setConfirming(true);
      const toPhilDocRef = doc(
        firestore,
        "transactions",
        "to-philippines-transactions"
      );
      await updateDoc(toPhilDocRef, {
        payouts: arrayUnion({
          date: new Date().toISOString(),
          totalShopDue: totalShopDue.toFixed(2),
          totalAffiliateDue: totalAffiliateDue.toFixed(2),
          totalElyDue: totalEly.toFixed(2),
          totalPayout: (totalShopDue + totalAffiliateDue + totalEly).toFixed(2),
          bookingIds: allBookingIds,
        }),
      });
      await addDoc(collection(firestore, "transactions"), {
        type: "toPhilippinesPayment",
        shopPayout: totalShopDue.toFixed(2),
        affiliatePayout: totalAffiliateDue.toFixed(2),
        elyPayout: totalEly.toFixed(2),
        totalPayout: (totalShopDue + totalAffiliateDue + totalEly).toFixed(2),
        bookingIds: allBookingIds,
        date: new Date().toISOString(),
      });
      setToPhilData({
        shopPayout: totalShopDue.toFixed(2),
        affiliatePayout: totalAffiliateDue.toFixed(2),
        elyPayout: totalEly.toFixed(2),
      });
      setConfirming(false);
    } catch (error) {
      console.error("Error confirming payments to Philippines:", error);
      setConfirming(false);
    }
  };

  // Placeholder next payout dates.
  const swissNextPayoutDate = "2025-03-24";
  const shopNextPayoutDate = "2025-03-26";
  const affiliateNextPayoutDate = "2025-03-26";
  const toPhilNextPayoutDate = "2025-03-24"; // Example

  return (
    <>
      <Header />
      <Container sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Payouts Page
        </Typography>
        {/* Swiss Company Section */}
        <Card sx={{ mb: 3, p: 2 }}>
          <Typography variant="h5" gutterBottom>
            Swiss Company Payouts
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="subtitle1">
              Next Payout Date: {toPhilNextPayoutDate}
            </Typography>
            <Typography variant="subtitle1">
              Total Amount for Shops: {totalShopDue.toFixed(2)}₱
            </Typography>
            <Typography variant="subtitle1">
              Total Amount for Affiliates: {totalAffiliateDue.toFixed(2)}₱
            </Typography>
            <Typography variant="subtitle1">
              Total Amount for Ely: {totalEly.toFixed(2)}₱
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              (Includes booking IDs: {allBookingIds.join(", ")})
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={confirmToPhilippinesPayments}
            disabled={confirming}
            sx={{ mt: 2 }}
          >
            {confirming ? "Confirming..." : "Confirm Payments"}
          </Button>
          {toPhilData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                Payments Confirmed: Shops: {toPhilData.shopPayout}, Affiliates:{" "}
                {toPhilData.affiliatePayout}, Ely: {toPhilData.elyPayout}
              </Typography>
            </Box>
          )}
        </Card>
        {/* Shop Payouts Section */}
        <Typography variant="h5" gutterBottom>
          Shop Payouts (Philippine Company to Rental Shops)
        </Typography>
        {loading ? (
          <CircularProgress />
        ) : shopPayouts.length === 0 ? (
          <Typography>No shop payouts due.</Typography>
        ) : (
          shopPayouts.map((shop) => (
            <Card key={shop.id} sx={{ mb: 2, p: 2 }}>
              <Typography variant="subtitle1">
                {shop.name}: {shop.amountDue.toFixed(2)}₱ due (Bookings between{" "}
                {new Date(shop.lastPayoutDate).toLocaleDateString()} and{" "}
                {new Date(shop.cutoffDate).toLocaleDateString()})
              </Typography>
              <Typography variant="caption">
                Next Payout Date: {shopNextPayoutDate}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Pending Payments: {shop.amountDue.toFixed(2)}₱
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => markShopPayoutDone(shop)}
                sx={{ mt: 1 }}
              >
                Mark Payout as Done
              </Button>
            </Card>
          ))
        )}
        {/* Affiliates Payouts Section */}
        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Affiliates Payouts (Philippine Company to Affiliates)
        </Typography>
        {loading ? (
          <CircularProgress />
        ) : affiliatePayouts.length === 0 ? (
          <Typography>No affiliate payouts due.</Typography>
        ) : (
          affiliatePayouts.map((aff) => (
            <Card key={aff.id} sx={{ mb: 2, p: 2 }}>
              <Typography variant="subtitle1">
                {aff.name}: {aff.amountDue.toFixed(2)}₱ due (Bookings between{" "}
                {new Date(aff.lastPayoutDate).toLocaleDateString()} and{" "}
                {new Date(aff.cutoffDate).toLocaleDateString()})
              </Typography>
              <Typography variant="caption">
                Next Payout Date: {affiliateNextPayoutDate}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Pending Payments: {aff.amountDue.toFixed(2)}₱
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => markAffiliatePayoutDone(aff)}
                sx={{ mt: 1 }}
              >
                Mark Payout as Done
              </Button>
            </Card>
          ))
        )}
      </Container>
      <Footer />
    </>
  );
};

export default PayoutsPage;
